/**
 * CommandBar — the center, full-row command surface, DOUBLE HEIGHT (Ori
 * 2026-07-24 — THE SEND IS A TASK): the TOP line is the task input (prompt /
 * search / jump; the `+` menu for files, workspaces, connectors, agents);
 * the BOTTOM line is the AGENT LANE — always at least one agent (the
 * platform agent), the send's delegation target. THE SEND IS AN EXPLICIT
 * TASK: world mints the keyed task from the message (kind by the
 * conversation's origin — sci//conversation:tab:@tab) and the task→agent
 * map (sci//task:@agent:@map**) assigns; picking an agent here is the
 * born-assigned carry (evidence data.agent) — auto leaves the map to decide.
 *
 * It listens for REPLY_EVENT (a box's reply action) to pre-fill the conversation.
 */
import React, { useEffect, useRef, useState } from "react";
import { Plus, ArrowUp, Search, Paperclip, LayoutDashboard, Cable, X, Reply, Bot } from "lucide-react";

import type { ChannelApi } from "./useChannel";
import { REPLY_EVENT } from "./AnnouncementStream";
import { createAgentAnnounce, humanAgentLabel } from "./agentSurface";
import { feedStore, useFeedStoreState, EMPTY_TAB_SLICE } from "./feedStore";

const newConversationId = (): string => `conv-${Date.now().toString(36)}`;

/** The platform agent — the guaranteed at-least-one of the agent lane (the
 * root sci::agent:causit row; chat messages map to it by skill). */
const PLATFORM_AGENT = "causit";

/** The AGENT ACTOR of an agent feed item — read from the item's own declared
 * `agentMachine`. An agent may wear ANY artifact (the row names it through
 * `sci//agent:artifact:@artifact`), so its identity is never recovered from
 * an artifact/conversation id. */
const agentMachineOfItem = (item: { agentMachine?: string } | undefined): string =>
  String(item?.agentMachine || "");

export const CommandBar: React.FC<{
  ch: ChannelApi;
  conversationId: string;
  setConversationId: (id: string, label?: string) => void;
  onNewConnector: () => void;
}> = ({
  ch,
  conversationId: activeConversationId,
  setConversationId: setActiveConversationId,
  onNewConnector,
}) => {
  const [text, setText] = useState("");
  const [composer, setComposer] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [conversationId, setConversationId] = useState("");
  const [candidates, setCandidates] = useState<unknown[]>([]);
  const [busy, setBusy] = useState(false);
  const feed = useFeedStoreState();
  // THE AGENT LANE — the send's delegation targets: a LIST, never an
  // either/or (sci//task:@agent:@map** is plural — the task HAS agents).
  // Empty = auto (the task→agent map decides: an agent conversation
  // delegates to ITS agent, everything else maps by skill — the platform
  // agent for messages). Clicks TOGGLE membership (the born-assigned carry).
  const [pickedAgents, setPickedAgents] = useState<string[]>([]);
  useEffect(() => {
    if (!ch.ready) return;
    // the known agents ride the served agent tab — one
    // drain per page lifetime, shared with the rail.
    void feedStore.ensureTab("agent", ch.query);
  }, [ch.ready, ch.query]);
  const agentItems = (feed.byTab["agent"] ?? EMPTY_TAB_SLICE).items;
  // "which agent's conversation am I in" is a LOOKUP over the served agent
  // items — the one whose conversation this is — never a parse of the id.
  const conversationAgent = agentMachineOfItem(
    agentItems.find((item) => item.conversationId === activeConversationId));
  const knownAgents = Array.from(new Set([
    ...(conversationAgent ? [conversationAgent] : []),
    PLATFORM_AGENT,
    ...agentItems.map(agentMachineOfItem).filter(Boolean),
  ]));
  // the AUTO target shown highlighted when nothing is picked: the agent
  // conversation's own agent, else the platform agent.
  const autoAgent = conversationAgent || PLATFORM_AGENT;
  // the REPLY stack (sci//announcement:reply:@announcement**): the announcements this command
  // replies to (ref + a label to SHOW it above the bar). A reply onto a reply ADDS
  // (never replaces); PREPEND so the array reversed is click order (first click last).
  const [reply, setReply] = useState<Array<{ ref: number | string; label: string }>>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setConversationId(activeConversationId);
  }, [activeConversationId]);

  // A box's reply action pre-fills the conversation + STACKS the reply-ref here.
  useEffect(() => {
    const onReply = (e: Event) => {
      const d = (e as CustomEvent).detail as { conversationId?: string; ref?: number | string; label?: string };
      if (d?.conversationId) setConversationId(d.conversationId);
      if (d?.ref !== undefined && d.ref !== null) {
        setReply((prev) => [{ ref: d.ref!, label: d.label || String(d.ref) }, ...prev.filter((b) => b.ref !== d.ref)]);
      }
    };
    window.addEventListener(REPLY_EVENT, onReply);
    return () => window.removeEventListener(REPLY_EVENT, onReply);
  }, []);

  // Search-as-you-type (jump/find), debounced.
  useEffect(() => {
    if (text.trim().length < 2) { setCandidates([]); return; }
    const t = setTimeout(async () => {
      const r = await ch.search(text.trim(), { limit: 6 }).catch(() => null);
      setCandidates(r?.candidates || []);
    }, 250);
    return () => clearTimeout(t);
  }, [text, ch]);

  const startNewWorkspace = () => {
    const cid = newConversationId();
    setConversationId(cid);
    setActiveConversationId(cid, "New workspace");
    setReply([]);
    setComposer(false);
  };

  const startNewConnector = () => {
    setComposer(false);
    onNewConnector();
  };

  // New agent (the + menu's Agent entry) — an inline name/skills form; the
  // create rides the SAME channel announce path (agentSurface.ts owns the
  // declared shape; the world owns the agent row mint).
  const [agentForm, setAgentForm] = useState(false);
  const [agentName, setAgentName] = useState("");
  const [agentSkills, setAgentSkills] = useState("");
  const [agentBusy, setAgentBusy] = useState(false);
  const [agentError, setAgentError] = useState("");

  const createAgent = async () => {
    if (agentBusy || !agentName.trim()) return;
    setAgentBusy(true);
    setAgentError("");
    try {
      const cid = conversationId.trim() || activeConversationId || newConversationId();
      if (cid !== activeConversationId) setActiveConversationId(cid);
      await ch.announce(createAgentAnnounce({
        name: agentName,
        skills: agentSkills.split(",").map((s) => s.trim()).filter(Boolean),
        conversationId: cid,
      }));
      setAgentName("");
      setAgentSkills("");
      setAgentForm(false);
      setComposer(false);
    } catch (e) {
      setAgentError(e instanceof Error ? e.message : String(e));
    } finally {
      setAgentBusy(false);
    }
  };

  const emit = async () => {
    if (busy || (!text.trim() && files.length === 0)) return;
    setBusy(true);
    try {
      const cid = conversationId.trim() || activeConversationId || newConversationId();
      if (cid !== activeConversationId) setActiveConversationId(cid);
      const recipient = [{ path: "channel/ws", kind: "channel", label: "ws" }];
      if (files.length > 0) {
        await ch.uploadFiles(files, { conversationId: cid });
      }
      if (text.trim()) {
        // OPTIMISTIC — the message shows immediately as a markdown artifact (status
        // start → complete | fail); the channel never waits/hides (the sync obeys it).
        // THE SEND IS A TASK: the picked agent rides as the born-assigned
        // carry; a NEW conversation declares its origin tab
        // (sci//conversation:tab:@tab).
        const isNewConversation = cid !== activeConversationId;
        const originTab = feed.feed?.middle || "";
        await ch.sendMessage(text.trim(), {
          conversationId: cid,
          reply: reply.length ? reply : undefined,  // the reply stack (announcement:reply:@announcement**) — ref + label for display
          recipient,
          ...(pickedAgents.length ? { agents: pickedAgents } : {}),
          ...(isNewConversation && originTab ? { tab: originTab } : {}),
        });
      }
      setText("");
      setFiles([]);
      setCandidates([]);
      setComposer(false);
      setReply([]);  // a sent command consumes the reply stack
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: "12px 24px 20px" }}>
      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        {/* REPLY stack (announcement:reply:@announcement**) — what this command replies to, shown
            above the bar; stacks (multiple), each removable. */}
        {reply.length > 0 && (
          <div style={{ marginBottom: 8, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 11, color: "var(--color-text-secondary, #9ca3af)" }}>Replying to</span>
            {reply.map((b) => (
              <span key={String(b.ref)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 8, border: "1px solid var(--color-border, #e5e7eb)", background: "var(--color-surface, #f8f9fa)", fontSize: 12, maxWidth: 320 }}>
                <Reply size={12} style={{ opacity: 0.6, flexShrink: 0 }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.label}</span>
                <button type="button" title="Remove reply" onClick={() => setReply((prev) => prev.filter((x) => x.ref !== b.ref))}
                  style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--color-text-secondary, #9ca3af)", padding: 0, display: "inline-flex" }}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        {/* search candidates (jump) */}
        {candidates.length > 0 && (
          <div style={{ marginBottom: 8, border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 10, overflow: "hidden" }}>
            {candidates.map((c: any, i) => (
              <button key={i} onClick={() => setText(c?.label || c?.title || "")} style={{ display: "flex", gap: 8, width: "100%", textAlign: "left", padding: "8px 12px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13, alignItems: "center" }}>
                <Search size={13} style={{ opacity: 0.5 }} />
                <span>{c?.label || c?.title || JSON.stringify(c)}</span>
              </button>
            ))}
          </div>
        )}

        {/* composer (the + tooltip) */}
        {composer && (
          <div style={composerMenu}>
            <div style={fileMenuRow}>
              <button type="button" onClick={() => fileRef.current?.click()} style={menuRowAction}>
                <Paperclip size={15} />
                <span>{files.length ? `${files.length} files attached` : "Attach file"}</span>
              </button>
              {files.length > 0 && (
                <button
                  type="button"
                  title="Remove files"
                  onClick={() => setFiles([])}
                  style={removeFilesButton}
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <button type="button" onClick={startNewWorkspace} style={menuRow}>
              <LayoutDashboard size={15} />
              <span>New workspace</span>
            </button>
            <button type="button" onClick={startNewConnector} style={menuRow}>
              <Cable size={15} />
              <span>New connector</span>
            </button>
            <button type="button" onClick={() => setAgentForm((v) => !v)} style={menuRow}>
              <Bot size={15} />
              <span>New agent</span>
            </button>
            {agentForm && (
              <div style={{ display: "grid", gap: 4, padding: "2px 4px 4px" }}>
                <input
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void createAgent(); } }}
                  placeholder="Agent name"
                  disabled={agentBusy}
                  style={agentInput}
                />
                <input
                  value={agentSkills}
                  onChange={(e) => setAgentSkills(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void createAgent(); } }}
                  placeholder="Skills (comma separated, optional)"
                  disabled={agentBusy}
                  style={agentInput}
                />
                <button
                  type="button"
                  onClick={() => void createAgent()}
                  disabled={agentBusy || !agentName.trim()}
                  style={{ ...menuRow, minHeight: 30, justifyContent: "center", border: "1px solid var(--color-border, #e5e7eb)", opacity: agentBusy || !agentName.trim() ? 0.5 : 1 }}
                >
                  Create agent
                </button>
                {agentError && <div style={{ fontSize: 11, color: "var(--color-error, #dc2626)" }}>{agentError}</div>}
              </div>
            )}
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </div>
        )}

        {/* THE BAR — DOUBLE HEIGHT: the task input on top, the agent lane
            below (always at least one agent — the delegation target). */}
        <div style={{ display: "grid", gap: 6, border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 14, padding: "8px 10px", background: "var(--color-bg, #fff)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => setComposer((v) => !v)} title="Attach file / new workspace / new connector / new agent" style={{ ...iconBtn, background: composer ? "var(--color-surface, #eef)" : "transparent" }}>
              <Plus size={18} />
            </button>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); emit(); } }}
              placeholder="Search anything · prompt anything · jump anywhere"
              style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 14, color: "inherit" }}
            />
            <button onClick={emit} disabled={busy || (!text.trim() && files.length === 0)} title="Send" style={{ ...iconBtn, background: text.trim() || files.length ? "var(--accent-primary, #6B7EEA)" : "transparent", color: text.trim() || files.length ? "#fff" : "inherit", opacity: busy ? 0.5 : 1 }}>
              <ArrowUp size={18} />
            </button>
          </div>
          {/* the agent lane — a LIST (never an either/or): clicks toggle
              membership; empty = auto (the auto target is highlighted). */}
          <div style={agentLane} aria-label="Agents">
            {knownAgents.map((agent) => {
              const picked = pickedAgents.includes(agent);
              const selected = pickedAgents.length ? picked : agent === autoAgent;
              return (
                <button
                  key={agent}
                  type="button"
                  onClick={() => setPickedAgents((held) => (
                    held.includes(agent) ? held.filter((a) => a !== agent) : [...held, agent]
                  ))}
                  title={picked
                    ? `Assigned to ${humanAgentLabel(agent)} — click to remove`
                    : `Assign to ${humanAgentLabel(agent)}`}
                  aria-pressed={selected}
                  style={{
                    ...agentChip,
                    ...(selected
                      ? { background: "color-mix(in srgb, var(--accent-primary, #6B7EEA) 14%, transparent)", borderColor: "var(--accent-primary, #6B7EEA)", color: "var(--accent-primary, #6B7EEA)" }
                      : {}),
                  }}
                >
                  <Bot size={12} aria-hidden="true" />
                  {humanAgentLabel(agent) || agent}
                  {selected && !pickedAgents.length && <span style={{ opacity: 0.6, fontSize: 10 }}>auto</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const iconBtn: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 10, border: "none", cursor: "pointer", color: "var(--color-text-secondary, #6b7280)" };
const agentLane: React.CSSProperties = {
  display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
  padding: "2px 2px 0", borderTop: "1px solid var(--color-border, #eef0f2)",
  paddingTop: 6,
};
const agentChip: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 5,
  padding: "3px 9px", borderRadius: 999,
  border: "1px solid var(--color-border, #e5e7eb)",
  background: "transparent", color: "var(--color-text-secondary, #6b7280)",
  fontSize: 12, cursor: "pointer",
};
const composerMenu: React.CSSProperties = {
  width: "min(260px, calc(100vw - 48px))",
  marginBottom: 8,
  padding: 6,
  border: "1px solid var(--color-border, #e5e7eb)",
  borderRadius: 10,
  display: "grid",
  gap: 4,
  background: "var(--color-surface, #f8f9fa)",
};
const menuRow: React.CSSProperties = {
  width: "100%",
  minHeight: 38,
  padding: "8px 10px",
  border: "none",
  borderRadius: 8,
  background: "transparent",
  color: "inherit",
  cursor: "pointer",
  fontSize: 13,
  display: "flex",
  alignItems: "center",
  gap: 9,
  textAlign: "left",
};
const fileMenuRow: React.CSSProperties = {
  ...menuRow,
  padding: 0,
  gap: 4,
};
const menuRowAction: React.CSSProperties = {
  minWidth: 0,
  flex: 1,
  alignSelf: "stretch",
  padding: "8px 10px",
  border: "none",
  borderRadius: 8,
  background: "transparent",
  color: "inherit",
  cursor: "pointer",
  fontSize: 13,
  display: "flex",
  alignItems: "center",
  gap: 9,
  textAlign: "left",
};
const agentInput: React.CSSProperties = {
  width: "100%",
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid var(--color-border, #e5e7eb)",
  background: "var(--color-bg, #fff)",
  color: "inherit",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};
const removeFilesButton: React.CSSProperties = {
  width: 22,
  height: 22,
  flex: "0 0 auto",
  borderRadius: 7,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--color-text-secondary, #6b7280)",
};
